import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { SystemAlertService } from "../../../../services/api/system-alert/sys-alert.service";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";

import { MessageService } from "@openng/optimus-ui/api";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { CheckboxModule } from "@openng/optimus-ui/checkbox";
import { SysAlert } from "../../../../models/api/sysAlert";
import { first } from "rxjs";
import { DatePickerModule } from "@openng/optimus-ui/datepicker";

@Component({
  selector: "app-system-alert",
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    InputTextModule,
    CheckboxModule,
    DatePickerModule,
  ],
  templateUrl: "./system-alert.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./system-alert.component.scss",
})
export class SystemAlertComponent {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);

  private message = inject(MessageService);
  private translate = inject(TranslateService);
  private systemAlertsService = inject(SystemAlertService);

  private actionType: "edit" | "add" = this.config.data?.actionType;

  private systemAlert: SysAlert = this.config.data?.systemAlert;
  public form = new FormGroup({
    message: new FormControl(this.systemAlert ? this.systemAlert.message : "", [
      Validators.required,
      Validators.minLength(5),
    ]),
    startTime: new FormControl(this.systemAlert ? new Date(this.systemAlert.startTime) : null, Validators.required),
    endTime: new FormControl(this.systemAlert ? new Date(this.systemAlert.endTime) : null, Validators.required),
    showInWeb: new FormControl(this.systemAlert ? this.systemAlert.showInWeb : false),
    showInApp: new FormControl(this.systemAlert ? this.systemAlert.showInApp : false),
  });

  public requestStarted = signal(false);

  /**
   * Save the system alert either as a new one or as an edit to an existing one.
   * If the form is valid and all required fields are filled, it sends the request
   * to the server to add or edit the system alert.
   */
  public saveSystemAlert() {
    const formValid = this.form.valid;
    const formValues = this.form.value;

    if (formValid && formValues.message && formValues.startTime && formValues.endTime) {
      const sysAlert: SysAlert = {
        message: formValues.message,
        startTime: formValues.startTime.getTime(),
        endTime: formValues.endTime.getTime(),
        showInApp: !!formValues.showInApp,
        showInWeb: !!formValues.showInWeb,
      };
      if (this.systemAlert?.id) {
        sysAlert.id = this.systemAlert.id;
      }

      switch (this.actionType) {
        case "add":
          this.addSystemAlert(sysAlert);
          break;
        case "edit":
          this.systemAlert = { ...sysAlert, id: this.systemAlert.id };
          this.editSystemAlert(this.systemAlert);
          break;
        default:
      }
    }
  }

  /**
   * Creates a new system alert.
   * @param sysAlert The system alert to create.
   * If the request is successful, it displays a success message and closes the dialog.
   * If the request is not successful, it displays an error message.
   */
  private addSystemAlert(sysAlert: SysAlert) {
    if (!this.requestStarted()) {
      this.requestStarted.set(true);

      this.systemAlertsService
        .createAlert(sysAlert)
        .pipe(first())
        .subscribe({
          next: () => {
            this.requestStarted.set(false);
            this.message.add({
              key: "global",
              severity: "success",
              detail: this.translate.instant("system_alerts.form.success_add"),
            });
            this.ref.close(true);
          },
          error: () => {
            this.requestStarted.set(false);
          },
        });
    }
  }

  /**
   * Edits a system alert.
   * @param sysAlert The system alert to edit.
   * If the request is successful, it displays a success message and closes the dialog.
   * If the request is not successful, it displays an error message.
   */
  private editSystemAlert(sysAlert: SysAlert) {
    if (!this.requestStarted()) {
      this.requestStarted.set(true);

      this.systemAlertsService
        .editAlert(sysAlert)
        .pipe(first())
        .subscribe({
          next: () => {
            this.requestStarted.set(false);
            this.message.add({
              key: "global",
              severity: "success",
              detail: this.translate.instant("system_alerts.form.success_edit"),
            });
            this.ref.close(true);
          },
          error: () => {
            this.requestStarted.set(false);
          },
        });
    }
  }
}
